/**
 * offlineQueue.js — IndexedDB-backed offline queue (falls back to localStorage).
 *
 * Stores pending API submissions per user. Each item carries the endpoint,
 * payload, and a monotonic timestamp so the sync engine can replay them in
 * order.
 */

const DB_NAME = 'VestaOfflineQueue';
const DB_VERSION = 2;
const STORE_NAME = 'queue';
const DEAD_LETTER_STORE_NAME = 'deadLetter';
const MAX_RETRY_COUNT = 3;

/* Backoff for transient (non-validation) failures — e.g. network/CORS/5xx —
 * that we never dead-letter but must stop re-attempting every single sync
 * cycle. Exponential, capped at 30 min, keyed off retryCount. */
const BASE_BACKOFF_MS = 30_000;
const MAX_BACKOFF_MS = 30 * 60_000;
const computeBackoffMs = (retryCount) =>
  Math.min(BASE_BACKOFF_MS * 2 ** Math.max(0, retryCount - 1), MAX_BACKOFF_MS);

/* ── helpers ─────────────────────────────────────────────────────────── */

let _db = null;

const openDB = () =>
  new Promise((resolve, reject) => {
    if (_db) return resolve(_db);
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      let store;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        store = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
      } else {
        store = e.target.transaction.objectStore(STORE_NAME);
      }

      if (!store.indexNames.contains('userId')) {
        store.createIndex('userId', 'userId', { unique: false });
      }

      if (!db.objectStoreNames.contains(DEAD_LETTER_STORE_NAME)) {
        const deadLetterStore = db.createObjectStore(DEAD_LETTER_STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
        deadLetterStore.createIndex('userId', 'userId', { unique: false });
      }
    };
    req.onsuccess = (e) => {
      _db = e.target.result;
      resolve(_db);
    };
    req.onerror = (e) => reject(e.target.error);
  });

let _idbAvailable = null;
const isIDBAvailable = async () => {
  if (_idbAvailable !== null) return _idbAvailable;
  try {
    await openDB();
    _idbAvailable = true;
  } catch {
    _idbAvailable = false;
  }
  return _idbAvailable;
};

/* localStorage fallback key */
const LS_KEY = '_vesta_offline_q';

const readLS = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeLS = (arr) => {
  localStorage.setItem(LS_KEY, JSON.stringify(arr));
};

const normalizeQueueItem = (item = {}) => ({
  ...item,
  userId: String(item.userId ?? ''),
  method: item.method || 'POST',
  ts: item.ts ?? Date.now(),
  retryCount: Number.isFinite(item.retryCount) ? item.retryCount : 0,
  maxRetries: Number.isFinite(item.maxRetries) ? item.maxRetries : MAX_RETRY_COUNT,
  firstQueuedAt: item.firstQueuedAt ?? item.ts ?? Date.now(),
  lastAttemptAt: item.lastAttemptAt ?? null,
  lastError: item.lastError ?? null,
  nextAttemptAt: item.nextAttemptAt ?? null,
  // Distinct from retryCount: counts ALL failures (validation + transient)
  // purely to size the backoff delay. retryCount is reserved for the
  // dead-letter threshold and must only ever count validation (400/422)
  // failures — mixing the two would let unrelated transient hiccups
  // (network/CORS/5xx) push a payload issue toward dead-letter early.
  failureCount: Number.isFinite(item.failureCount) ? item.failureCount : 0,
  requestType: item.requestType ?? 'generic',
  clientRequestId:
    item.clientRequestId ??
    `rq_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`,
});

const shouldMoveToDeadLetter = (item = {}) =>
  (item.retryCount ?? 0) >= (item.maxRetries ?? MAX_RETRY_COUNT);

const normalizeDeadLetterItem = (item = {}, reason = null) => ({
  ...normalizeQueueItem(item),
  deadLetteredAt: Date.now(),
  deadLetterReason: reason ?? item.lastError ?? 'Moved to dead-letter queue',
});

/* ── public API ──────────────────────────────────────────────────────── */

/**
 * Enqueue a pending request.
 * @param {string} userId
 * @param {string} url      — API endpoint (e.g. "/fbs/public/create")
 * @param {object} payload  — JSON body
 * @returns {Promise<void>}
 */
export const enqueue = async (userId, url, payload, meta = {}) => {
  const item = normalizeQueueItem({
    userId: String(userId),
    url,
    payload,
    ts: Date.now(),
    ...meta,
  });

  if (await isIDBAvailable()) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).add(item);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  /* fallback */
  const arr = readLS();
  item.id = Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  arr.push(item);
  writeLS(arr);
};

/**
 * Return all pending items for a user, sorted oldest-first.
 * For legacy items that pre-date the clientRequestId field, the generated id
 * is written back to the store so it remains stable across retry attempts.
 */
export const getAll = async (userId) => {
  const uid = String(userId);

  if (await isIDBAvailable()) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      // Use readwrite so we can persist a stable clientRequestId for legacy items.
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.index('userId').getAll(uid);
      let normalizedItems = [];

      req.onsuccess = () => {
        normalizedItems = (req.result || []).map((item) => {
          const normalized = normalizeQueueItem(item);
          if (!item.clientRequestId) {
            // Write the generated id back so subsequent retries send the same
            // X-Offline-Request-Id header (idempotency / deduplication).
            store.put(normalized);
          }
          return normalized;
        });
        normalizedItems.sort((a, b) => a.ts - b.ts);
      };

      tx.oncomplete = () => resolve(normalizedItems);
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  /* localStorage fallback — also write back any missing clientRequestIds */
  const arr = readLS();
  let changed = false;
  const updated = arr.map((item) => {
    if (!item.clientRequestId) {
      changed = true;
      return normalizeQueueItem(item);
    }
    return item;
  });
  if (changed) writeLS(updated);

  return updated
    .filter((i) => String(i.userId) === uid)
    .sort((a, b) => a.ts - b.ts);
};

/**
 * Remove a single item by its id (after successful server POST).
 */
export const remove = async (id) => {
  if (await isIDBAvailable()) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.objectStore(STORE_NAME).delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  const arr = readLS().filter((i) => i.id !== id);
  writeLS(arr);
};

export const moveToDeadLetter = async (item, reason = null) => {
  const deadLetterItem = normalizeDeadLetterItem(item, reason);

  if (await isIDBAvailable()) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_NAME, DEAD_LETTER_STORE_NAME], 'readwrite');
      tx.objectStore(DEAD_LETTER_STORE_NAME).add(deadLetterItem);
      if (item?.id !== undefined && item?.id !== null) {
        tx.objectStore(STORE_NAME).delete(item.id);
      }
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  const activeItems = readLS();
  const nextActiveItems = activeItems.filter((queuedItem) => queuedItem.id !== item?.id);
  const deadLetterItems = readLSDeadLetter();
  deadLetterItems.push({
    ...deadLetterItem,
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    originalQueueId: item?.id ?? null,
  });
  writeLS(nextActiveItems);
  writeLSDeadLetter(deadLetterItems);
};

/**
 * Record a failed attempt.
 * @param {*}       id
 * @param {string}  [errorMessage]
 * @param {object}  [options]
 * @param {boolean} [options.countTowardRetry=true] — whether this failure
 *   counts toward the dead-letter threshold (retryCount). Pass `false` for
 *   transient/non-validation failures so they only affect backoff timing
 *   (failureCount) and never push an item toward dead-letter.
 */
export const markAttempt = async (id, errorMessage = null, { countTowardRetry = true } = {}) => {
  const buildUpdate = (item) => {
    const retryCount = countTowardRetry ? (item.retryCount ?? 0) + 1 : (item.retryCount ?? 0);
    const failureCount = (item.failureCount ?? 0) + 1;
    return normalizeQueueItem({
      ...item,
      retryCount,
      failureCount,
      lastAttemptAt: Date.now(),
      lastError: errorMessage,
      nextAttemptAt: Date.now() + computeBackoffMs(failureCount),
    });
  };

  if (await isIDBAvailable()) {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(id);
      req.onsuccess = () => {
        const item = req.result;
        if (!item) return; // item not found — let tx.oncomplete resolve consistently
        store.put(buildUpdate(item));
      };
      tx.oncomplete = () => resolve();
      tx.onerror = (e) => reject(e.target.error);
    });
  }

  const arr = readLS();
  const next = arr.map((item) => (item.id !== id ? item : buildUpdate(item)));
  writeLS(next);
};

export const isPoisoned = (item) => shouldMoveToDeadLetter(normalizeQueueItem(item));

/** True if a transient-failure item's backoff window hasn't elapsed yet. */
export const isDue = (item) => {
  const nextAttemptAt = item?.nextAttemptAt;
  return !nextAttemptAt || nextAttemptAt <= Date.now();
};

const LS_DEAD_LETTER_KEY = '_vesta_offline_q_dead';

const readLSDeadLetter = () => {
  try {
    return JSON.parse(localStorage.getItem(LS_DEAD_LETTER_KEY) || '[]');
  } catch {
    return [];
  }
};

const writeLSDeadLetter = (arr) => {
  localStorage.setItem(LS_DEAD_LETTER_KEY, JSON.stringify(arr));
};

export const count = async (userId) => {
  const items = await getAll(String(userId));
  return items.length;
};

/**
 * Count items actually eligible to attempt right now (excludes items still
 * within their transient-failure backoff window). Used to decide whether a
 * sync pass has anything real to do, distinct from `count()` which includes
 * backed-off items and is used for the "queue is empty" reachability check.
 */
export const countDue = async (userId) => {
  const items = await getAll(String(userId));
  return items.filter(isDue).length;
};
